import { useState } from 'react'
import useSWR from 'swr'
import { useDebounce } from 'use-debounce'

import { useDependencies } from '@/components/providers/DependencyProvider'

// TODO: These all probably fail if not logged in, probably should have some handling for that in some way.

interface SWRResponse<T> {
	data?: T,
	isValidating: boolean,
}

type PaginatedData<T, U extends string> = SWRResponse<Array<T>> & {
	pageData?: {
		current: number,
		total: number,
		from: number,
		to: number,

		hasNextPage: boolean,
		hasPrevPage: boolean,
	},

	search: string,
	sort: U,
	isDescending: boolean,

	nextPage: () => void,
	prevPage: () => void,

	setSearch: (search: string) => void,
	setSort: (sort: U, isDescending: boolean) => void,
}

interface Options<T> {
	limit: number,
	initialSort: T,
	initialSearch: string,
	initiallyDescending: boolean,
	initialPage: number,
}

function usePaginatedDataEndpoint<T, U extends string>(
	endpoint: string,
	{ limit, initialSort, initialSearch, initiallyDescending, initialPage }: Options<U>,
): PaginatedData<T, U> {
	const { apiFetcher } = useDependencies()
	const [page, setPage] = useState(initialPage)
	const [search, setSearch] = useState(initialSearch)
	const [debouncedSearch] = useDebounce(search, 400)
	const [sort, setSort] = useState<U>(initialSort)
	const [isDescending, setIsDecending] = useState(initiallyDescending)
	const { data, isValidating } = useSWR<{
		data: Array<T>,

		current_page: number,
		last_page: number,
		per_page: number,
		total: number,

		from: number,
		to: number,

		next_page_url: string | null,
		prev_page_url: string | null,
	}>(
		`${endpoint}?page=${page}&search=${debouncedSearch}&limit=${limit}&sort=${sort}${isDescending ? ':DESC' : ':ASC'}`,
		apiFetcher,
	)

	return {
		data: data?.data,
		isValidating,

		pageData: data && {
			current: data.current_page,
			total: data.total,
			from: data.from,
			to: data.to,

			hasNextPage: data.next_page_url !== null,
			hasPrevPage: data.prev_page_url !== null,
		},

		search,
		sort,
		isDescending,

		nextPage() {
			if (data?.next_page_url !== null) {
				setPage(page + 1)
			}
		},
		prevPage() {
			if (data?.prev_page_url !== null) {
				setPage(page - 1)
			}
		},

		setSearch(search: string) {
			setSearch(search)
		},
		setSort(sort: U, isDescending: boolean) {
			setIsDecending(isDescending)
			setSort(sort)
		},
	}
}


export function useOrders(options: Options<OrderSort>): PaginatedData<Order, OrderSort> {
	const result = usePaginatedDataEndpoint<Omit<Order, 'order_date'> & { order_date: string | null }, OrderSort>(
		'/orders',
		options,
	)

	return Object.assign(result, {
		data: result.data?.map(order => Object.assign(order, {
			order_date: order.order_date === null ? null : new Date(order.order_date),
		})),
	})
}

export function useOrder(orderId: string): SWRResponse<Order> {
	const { apiFetcher } = useDependencies()
	const { data, isValidating } = useSWR<{
		data: Omit<Order, 'order_date'> & { order_date: string | null },
	}>(`/orders/${orderId}`, apiFetcher)

	return {
		data: data && Object.assign(data.data, {
			order_date: data.data.order_date === null ? null : new Date(data.data.order_date),
		}),

		isValidating,
	}
}


export function useInvoices(options: Options<InvoiceSort>): PaginatedData<Invoice, InvoiceSort> {
	const result = usePaginatedDataEndpoint<Omit<Invoice, 'invoice_date' | 'order_date' | 'due_date'> & { invoice_date: string, order_date: string, due_date: string }, InvoiceSort>(
		'/invoices',
		options,
	)

	return Object.assign(result, {
		data: result.data?.map(invoice => Object.assign(invoice, {
			due_date: new Date(invoice.due_date),
			invoice_date: new Date(invoice.invoice_date),
			order_date: new Date(invoice.order_date),
		})),
	})
}

export function useInvoice(invoiceId: string): SWRResponse<Invoice> {
	const { apiFetcher } = useDependencies()
	const { data, isValidating } = useSWR<{
		data: Omit<Invoice, 'invoice_date' | 'order_date' | 'due_date'> & { invoice_date: string, order_date: string, due_date: string },
	}>(`/invoices/${invoiceId}`, apiFetcher)

	return {
		data: data && Object.assign(data.data, {
			due_date: new Date(data.data.due_date),
			invoice_date: new Date(data.data.invoice_date),
			order_date: new Date(data.data.order_date),
		}),

		isValidating,
	}
}


export function usePayments(options: Options<PaymentSort>): PaginatedData<Payment, PaymentSort> {
	const result = usePaginatedDataEndpoint<Omit<Payment, 'payment_date'> & { payment_date: string }, PaymentSort>(
		'/payments',
		options,
	)

	return Object.assign(result, {
		data: result.data?.map(payment => Object.assign(payment, {
			payment_date: new Date(payment.payment_date),
		})),
	})
}


export function useSerials(options: Options<SerialSort>): PaginatedData<Serial, SerialSort> {
	const result = usePaginatedDataEndpoint<Omit<Serial, 'invoice_date'> & { invoice_date: string }, SerialSort>(
		'/serials',
		options,
	)

	return Object.assign(result, {
		data: result.data?.map(serial => Object.assign(serial, {
			invoice_date: new Date(serial.invoice_date),
		})),
	})
}


export const useShipments = useInvoices
export const useShipment = useInvoice


export function useBilling(): SWRResponse<Billing> {
	const { apiFetcher } = useDependencies()
	const { data, isValidating } = useSWR<{
		data: Omit<Billing, 'payment_date'> & { payment_date: string | null },
	}>('/billing', apiFetcher)

	return {
		data: data && Object.assign(data.data, {
			payment_date: data.data.payment_date === null ? null : new Date(data.data.payment_date),
		}),

		isValidating,
	}
}
